
import Vue from "vue";
import AppAssessmentListingFilterHeader from "@/components/recruiter/app_assessment/AppAssessmentListingFilterHeader.vue";
import GoBackHeader from "@/components/shared/GoBackHeader.vue";
import { AppAssessments } from "@/interfaces/data_objects/app_assessments";
import { AppAssessmentNames } from "@/store/modules/common/interfaces";
import { mapActions, mapGetters, mapMutations } from "vuex";
import DeleteItem from "@/components/shared/DeleteItem.vue";
import {
  APP_ASSESSMENTS,
  SITE_DIRECTION
} from "@/store/modules/common/constants";
import { AssessmentListingHeaderOptions } from "@/interfaces/responses/assessments/assessments";
import {
  ACTIVE_ASSESSMENT,
  APP_AI_ASSESSMENT_LISTING,
  RESET_AI_ASSESSMENT
} from "@/store/modules/recruiter/constants";
import { SiteDirections } from "@/store/modules/common/interfaces";
import {
  format_date,
  get_app_assessment_status,
  get_app_assessment_status_color,
  get_color_for_percentage,
  get_pass_or_fail_class,
  assessment_pass_fail_result
} from "@/utils/global";
import { get_color_by_name } from "@/utils/colors";
import DataNotFound from "@/components/shared/DataNotFound.vue";
import { ROOT_ERROR, ROOT_NOTIFICATION } from "@/store/modules/root/constants";
import { AppAiAssessmentListing } from "@/store/modules/recruiter/interfaces";
import { User } from "@/interfaces/data_objects/user";
import OnBoardingInvitationCard from "@/components/recruiter/OnBoardingInvitationCard.vue";
import { USER_DETAILS_BY_ID } from "@/store/modules/auth/constants";
import RefreshButton from "@/components/shared/RefreshButton.vue";
import { AI_ASSESSMENT_SORT_CONFIG } from "../../../../utils/assessment_sort_config";

export default Vue.extend({
  name: "AppAiAssessmentReport",
  components: {
    AppAssessmentListingFilterHeader,
    GoBackHeader,
    DataNotFound,
    DeleteItem,
    OnBoardingInvitationCard,
    RefreshButton
  },
  data() {
    return {
      loading: false, // Loading state of the page
      data_error: false, // Error state of the page
      assessments: [] as AppAssessments.Assessment[],
      table_headers: [
        {
          title: this.$t("assessments.ai-listing.col-1").toString(),
          cols: 1,
          direction: "asc"
        },
        {
          title: this.$t("assessments.ai-listing.col-2").toString(),
          cols: 2,
          sortable: true,
          direction: "desc"
        },
        { title: this.$t("assessments.ai-listing.col-3").toString(), cols: 2 },
        {
          title: this.$t("assessments.ai-listing.col-5").toString(),
          cols: 2,
          sortable: true,
          direction: "asc"
        },
        { title: this.$t("assessments.ai-listing.col-6").toString(), cols: 3 },
        {
          title: this.$t("assessments.ai-listing.col-7").toString(),
          cols: 1,
          is_btn: true
        }
      ] as AssessmentListingHeaderOptions[],
      delete_icon: require("@/assets/icons/bold/reset.svg"),
      is_delete: false,
      selected_user: {
        id: 0,
        last_name: "",
        first_name: "",
        avatar_uri: ""
      } as AppAssessments.AssessmentUserObject,
      selected_assessment: null as AppAssessments.AiAssessmentResults | null,
      delete_btn_loading: false,
      on_boarding_invitation: false,
      user_object: {} as User,
      status: "",
      sort_index: 3,
      sort_field_config: AI_ASSESSMENT_SORT_CONFIG
    };
  },
  watch: {
    "CurrentAssessment.selected_dates.date_filter": function (
      new_value: string[],
      old_value: string[]
    ) {
      if (
        (new_value.length === 2 || new_value.length === 0) &&
        new_value.length !== old_value.length
      )
        this.process_assessment_listing(1);
    },
    "CurrentAssessment.selected_candidates": function (
      new_value: string[],
      old_value: string[]
    ) {
      if (JSON.stringify(new_value) !== JSON.stringify(old_value))
        this.process_assessment_listing(1);
    },
    get_site_direction() {
      this.table_headers = [
        {
          title: this.$t("assessments.ai-listing.col-1").toString(),
          cols: 1,
          sortable: true,
          direction: "asc"
        },
        {
          title: this.$t("assessments.ai-listing.col-2").toString(),
          cols: 2,
          sortable: true,
          direction: "asc"
        },
        { title: this.$t("assessments.ai-listing.col-3").toString(), cols: 2 },
        {
          title: this.$t("assessments.ai-listing.col-5").toString(),
          cols: 3,
          sortable: true,
          direction: "asc"
        },
        { title: this.$t("assessments.ai-listing.col-6").toString(), cols: 3 },
        {
          title: this.$t("assessments.ai-listing.col-7").toString(),
          cols: 1,
          is_btn: true
        }
      ];
    }
  },
  computed: {
    SiteDirections() {
      return SiteDirections;
    },
    ...mapGetters("common", {
      app_assessments: APP_ASSESSMENTS,
      get_site_direction: SITE_DIRECTION
    }),
    ...mapGetters("recruiter", {
      app_ai_assessment_listing: APP_AI_ASSESSMENT_LISTING
    }),
    not_found_error() {
      return this.$t("errors.ai-assessments-not-found").toString();
    },
    AppAssessmentsStatus() {
      return AppAssessments.AppAssessmentStatus;
    },
    CurrentAssessment(): AppAiAssessmentListing {
      return this.app_ai_assessment_listing();
    }
  },
  async mounted() {
    await this.init_process();
  },
  methods: {
    format_date,
    get_app_assessment_status,
    get_app_assessment_status_color,
    get_color_for_percentage,
    get_pass_or_fail_class,
    assessment_pass_fail_result,
    ...mapActions("recruiter", {
      fetch_ai_assessment_listing: APP_AI_ASSESSMENT_LISTING,
      reset_ai_assessment: RESET_AI_ASSESSMENT
    }),
    ...mapMutations("recruiter", {
      set_active_assessment: ACTIVE_ASSESSMENT
    }),
    ...mapMutations({
      set_root_notification: ROOT_NOTIFICATION,
      set_root_error: ROOT_ERROR
    }),
    ...mapActions("auth", {
      fetch_user_by_id: USER_DETAILS_BY_ID
    }),
    async init_process() {
      this.loading = true;
      const staticAsssessments = Object.values(AppAssessmentNames).map(
        (val: string) => val
      );
      this.assessments = this.app_assessments.filter(
        (val: AppAssessments.Assessment) =>
          !staticAsssessments.includes(val.assessment_type.toString())
      );
      // If assessment is not found
      if (!this.assessments.length) {
        this.data_error = true;
        this.loading = false;
        return;
      }

      await this.fetch_assessment_by_pagination(
        this.CurrentAssessment.page,
        this.CurrentAssessment.limit
      );
    },
    async process_assessment_listing(page: number) {
      if (!this.assessments.length) return;
      await this.fetch_assessment_by_pagination(
        page,
        this.CurrentAssessment.limit
      );
    },
    async fetch_assessment_by_pagination(page = 1, limit: number) {
      this.loading = true;
      const assessment_ids = this.assessments.map(
        (assessment: AppAssessments.Assessment) => assessment.id
      );
      const res = await this.fetch_ai_assessment_listing({
        page,
        limit,
        assessment_ids,
        selected_dates: this.CurrentAssessment.selected_dates,
        selected_candidates: this.CurrentAssessment.selected_candidates,
        score_filter: this.CurrentAssessment.score_filter,
        status_filter: this.CurrentAssessment.status_filter,
        sort_by: this.CurrentAssessment.sort_by,
        sort_order: this.CurrentAssessment.sort_order
      });
      if (!res) {
        this.data_error = true;
        this.loading = false;
        return;
      }
      this.loading = false;
    },
    transform_view_icon(icon: HTMLElement): HTMLElement {
      const color = get_color_by_name("success");
      for (let node of icon.children) node.setAttribute("stroke", color);
      return icon;
    },
    view_assessment_result(assessment_id: number, user_id: number) {
      this.$router.push(
        `/recruiter/assessments/ai/${assessment_id}/view/${user_id}`
      );
    },
    transform_trash_icon(icon: HTMLElement): HTMLElement {
      const color = "#FF4066";
      for (let node of icon.children) node.setAttribute("stroke", color);
      return icon;
    },
    delete_assessment(assessment: AppAssessments.AiAssessmentResults) {
      this.selected_user = assessment.user;
      this.selected_assessment = assessment;
      this.delete_btn_loading = false;
      this.is_delete = true;
    },
    async dialog_action(value: boolean) {
      if (value) {
        this.delete_btn_loading = true;
        const response = await this.reset_ai_assessment({
          assessment_id: this.selected_assessment?.assessment?.id,
          user_id: this.selected_user.id
        });
        if (response) {
          this.set_root_notification(
            `${this.selected_user.first_name} ${
              this.selected_user.last_name
            } ${this.$t("assessments.assessment-reset")}`
          );
          this.process_assessment_listing(1);
        } else {
          this.set_root_error(
            `${this.selected_user.first_name} ${
              this.selected_user.last_name
            } ${this.$t("assessments.assessment-reset-failed")}`
          );
        }
        this.is_delete = false;
      }
    },
    update_filter(filter: string) {
      this.CurrentAssessment.score_filter = filter;
      this.process_assessment_listing(1);
    },
    async sort_assessment(ind: number) {
      this.table_headers[ind].direction =
        this.table_headers[ind].direction === "asc" ? "desc" : "asc";
      const sortField = this.sort_field_config[ind];

      if (sortField) {
        this.CurrentAssessment.sort_by = sortField;
        this.CurrentAssessment.sort_order = this.table_headers[ind].direction;

        await this.fetch_assessment_by_pagination(
          this.CurrentAssessment.page,
          this.CurrentAssessment.limit
        );
      }
    },
    async open_user_details(
      user: AppAssessments.AssessmentUserObject,
      status: AppAssessments.AppAssessmentStatus | null
    ) {
      const response = await this.fetch_user_by_id(user.id);
      if (response) {
        this.user_object = response;
        this.on_boarding_invitation = true;
        this.status = status || "";
      }
    },
    update_status_filter(filter: string) {
      this.CurrentAssessment.status_filter = filter;
      this.process_assessment_listing(1);
    },
    get_percentage(
      assessment: AppAssessments.AiAssessmentResults
    ): number | null {
      if (
        assessment.result &&
        assessment.result.overall_score !== null &&
        assessment.result.overall_score !== undefined
      ) {
        const rawScore = assessment.result.overall_score;
        const parsedScore =
          typeof rawScore === "string" ? parseFloat(rawScore) : rawScore;
        if (isNaN(parsedScore)) {
          return null;
        }
        // Cap percentage at 100% to ensure scores are out of 100%
        return Math.min(parsedScore, 100);
      }
      return null;
    },
    check_status(assessment: AppAssessments.AiAssessmentResults) {
      if (assessment.status)
        return [
          AppAssessments.AppAssessmentStatus.IN_PROGRESS,
          AppAssessments.AppAssessmentStatus.CANCELLED,
          AppAssessments.AppAssessmentStatus.FAILED
        ].includes(assessment.status);
      return false;
    },
    refresh_listing() {
      if (this.assessments.length)
        this.fetch_assessment_by_pagination(
          this.CurrentAssessment.page,
          this.CurrentAssessment.limit
        );
    }
  }
});
